import FilterAltOutlinedIcon from "@mui/icons-material/FilterAltOutlined";
import { Autocomplete, Box, Button, Chip, Divider, Menu, Stack, TextField } from "@mui/material";
import { useCallback, useMemo, useRef, useState } from "react";
import { FilterSection } from "../../filter/filter-section";
import { TagsFilter } from "../../tags/tags-filter";

import type { Dispatch, FC, SetStateAction } from "react";

function toOptions(contentTypes: string[]): { [key: string]: string } {
  return contentTypes.reduce<{ [key: string]: string }>((acc, key) => {
    acc[key] = key;
    return acc;
  }, {});
}

const NO_TAGS: string[] = [];

export const ContentLibraryFilter: FC<{
  handleApplyFilter: () => void;
  selectedContentTypes: string[];
  setSelectedContentTypes: Dispatch<SetStateAction<string[]>>;
  contentTypes: string[];
  selectedTags?: string[];
  setSelectedTags?: Dispatch<SetStateAction<string[]>>;
  availableTags?: string[];
}> = ({
  handleApplyFilter,
  selectedContentTypes,
  setSelectedContentTypes,
  contentTypes,
  selectedTags = NO_TAGS,
  setSelectedTags,
  availableTags = NO_TAGS,
}) => {
  const [dialog, setDialog] = useState<boolean>(false);
  const filterIcon = useRef(null);

  const handleApply = useCallback(() => {
    handleApplyFilter();
    setDialog(false);
  }, [handleApplyFilter]);

  const handleClose = useCallback(() => {
    setDialog(false);
  }, [setDialog]);

  const handleSelectAllContentTypes = useCallback(() => {
    setSelectedContentTypes(contentTypes);
  }, [contentTypes, setSelectedContentTypes]);

  const handleDeselectAllContentTypes = useCallback(() => {
    setSelectedContentTypes([]);
  }, [setSelectedContentTypes]);

  const isAllContentTypesSelected = useMemo(
    () => selectedContentTypes.length === contentTypes.length,
    [selectedContentTypes, contentTypes],
  );

  const isNoContentTypesSelected = useMemo(
    () => selectedContentTypes.length === 0,
    [selectedContentTypes],
  );

  const hasTagFiltering = setSelectedTags && availableTags.length > 0;

  return (
    <>
      <Box ref={filterIcon}>
        <Button
          data-analytics-id="content-library-filter"
          onClick={() => setDialog(true)}
          startIcon={<FilterAltOutlinedIcon />}
        >
          Filter
        </Button>
      </Box>
      <Menu
        // oxlint-disable-next-line react/refs -- MUI anchorEl needs the DOM node; the popover only opens after the anchor has mounted
        anchorEl={filterIcon.current}
        open={dialog}
        onClose={() => setDialog(false)}
        aria-labelledby="alert-dialog-title"
        aria-describedby="alert-dialog-description"
      >
        <Box
          sx={{
            backgroundColor: "background.paper",
            color: "text.primary",
            pb: 3,
            px: 3,
          }}
        >
          <Stack
            sx={{
              display: "flex",
              flexWrap: "wrap",
            }}
          >
            <Stack direction="row" spacing={2} pt={2}>
              <Button
                size="small"
                disabled={isAllContentTypesSelected}
                onClick={handleSelectAllContentTypes}
                data-analytics-id="content-library-filter-select-all-content-types"
              >
                Select All
              </Button>
              <Button
                size="small"
                disabled={isNoContentTypesSelected}
                onClick={handleDeselectAllContentTypes}
                data-analytics-id="content-library-filter-deselect-all-content-types"
              >
                Deselect All
              </Button>
            </Stack>
            <FilterSection
              title="Content types"
              options={toOptions(contentTypes)}
              selectedItems={selectedContentTypes}
              onChange={(values) => {
                setSelectedContentTypes(values);
              }}
              omitAllOption
            />

            {hasTagFiltering && (
              <Box sx={{ m: 3, minWidth: 180, maxWidth: 250 }}>
                <TagsFilter />
                <Autocomplete
                  multiple
                  disableClearable
                  id="tag-filter-select"
                  value={selectedTags}
                  options={availableTags}
                  renderOption={(renderProps, option) => (
                    <Box component="li" {...renderProps} key={option}>
                      <Chip label={option} color="primary" />
                    </Box>
                  )}
                  renderValue={() => null}
                  renderInput={(params) => (
                    <TextField
                      label="Search tags"
                      {...params}
                      value=""
                      slotProps={{
                        input: { ...params.InputProps, "data-testid": "tag-filter-select" },
                      }}
                    />
                  )}
                  getOptionLabel={(option) => option}
                  onChange={(_, values) => {
                    if (setSelectedTags) {
                      setSelectedTags(values);
                    }
                  }}
                />
                {selectedTags.length > 0 && (
                  <Stack
                    direction="row"
                    mt={2}
                    gap={1}
                    useFlexGap
                    flexWrap="wrap"
                    sx={{ maxWidth: "100%" }}
                  >
                    {selectedTags.map((tag, index) => (
                      <Chip
                        // oxlint-disable-next-line react/no-array-index-key -- Tag names are not guaranteed unique here; the items are stateless
                        key={index}
                        label={tag}
                        color="primary"
                        onDelete={() => {
                          if (setSelectedTags) {
                            setSelectedTags(selectedTags.filter((tagValue) => tagValue !== tag));
                          }
                        }}
                      />
                    ))}
                  </Stack>
                )}
              </Box>
            )}
          </Stack>
          <Divider />
          <Box
            sx={{
              alignItems: "center",
              display: "flex",
              flexWrap: "wrap",
              pt: 3,
            }}
          >
            <Box sx={{ flexGrow: 1 }} />

            <Button
              data-analytics-id="content-library-filter-menu-cancel"
              variant="outlined"
              type="reset"
              onClick={handleClose}
            >
              Cancel
            </Button>

            <Button
              data-analytics-id="content-library-filter-menu-apply"
              type="submit"
              sx={{ ml: 2 }}
              variant="contained"
              onClick={handleApply}
            >
              Apply Filter
            </Button>
          </Box>
        </Box>
      </Menu>
    </>
  );
};
